import { useNavigate, useParams } from '@tanstack/react-router';
import { RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { CollectionKind, type Collection } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useRuleFields } from '@/features/product-rules';
import { ViewOnStorefrontButton } from '@/features/stores';
import { useStore } from '@/shared/store/store-context';

import {
  emptyCollectionForm,
  fromCollection,
  toCollectionCreate,
  toCollectionUpdate,
} from '../api/mappers';
import { CollectionForm } from '../components/collection-form';
import { CollectionKindBadge } from '../components/collection-kind-badge';
import { CollectionProductsList } from '../components/collection-products-list';
import { CollectionStatusBadge } from '../components/collection-status-badge';
import { DeleteCollectionDialog } from '../components/delete-collection-dialog';
import { ManualProductsSection } from '../components/manual-products-section';
import { useCollection, useCollectionProducts } from '../hooks/use-collection';
import {
  useCreateCollection,
  useRefreshCollection,
  useUpdateCollection,
} from '../hooks/use-collection-mutations';

const EDIT_ROUTE = '/_authed/_active/s/$storeId/collections/$collectionId' as const;

function KindPicker({
  value,
  onChange,
}: {
  value: CollectionKind;
  onChange: (kind: CollectionKind) => void;
}) {
  const { t } = useTranslation('collections');
  return (
    <FormSection
      title={t('create.kind')}
      description={t('create.description')}
      tour="collections.new.kind"
    >
      <RadioGroup
        value={value}
        onValueChange={(v) => onChange(v as CollectionKind)}
        className="grid gap-2 sm:grid-cols-2"
        aria-label={t('create.kind')}
      >
        {Object.values(CollectionKind).map((kind) => (
          <label
            key={kind}
            htmlFor={`collection-kind-${kind}`}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-md border p-3',
              value === kind && 'border-primary bg-primary-tint/40',
            )}
          >
            <RadioGroupItem id={`collection-kind-${kind}`} value={kind} className="mt-0.5" />
            <span className="grid gap-0.5 text-sm">
              <span className="font-medium">{t(`kind.${kind}`)}</span>
              <span className="text-muted-foreground text-xs">{t(`kindHint.${kind}`)}</span>
            </span>
          </label>
        ))}
      </RadioGroup>
    </FormSection>
  );
}

function CollectionCreate() {
  const { t } = useTranslation('collections');
  const store = useStore();
  const navigate = useNavigate();
  const create = useCreateCollection(store.id);
  const [kind, setKind] = useState<CollectionKind>('manual');

  return (
    <PageContent>
      <PageHeader
        title={t('create.title')}
        backTo={{ to: '/s/$storeId/collections', params: { storeId: store.id } }}
        backLabel={t('title')}
      />
      <div className="flex flex-col gap-6">
        <KindPicker value={kind} onChange={setKind} />
        <CollectionForm
          key={kind}
          storeId={store.id}
          currency={store.defaultCurrency}
          mode="create"
          defaultValues={emptyCollectionForm(kind)}
          isSubmitting={create.isPending}
          onSubmit={async (values, kindOf) => {
            const created = await create.mutateAsync(toCollectionCreate(values, kindOf));
            await navigate({
              to: '/s/$storeId/collections/$collectionId',
              params: { storeId: store.id, collectionId: created.id },
              replace: true,
            });
          }}
        >
          {kind === 'manual' ? (
            <FormSection title={t('sections.products')}>
              <p className="text-muted-foreground text-sm">{t('create.manualProductsAfterSave')}</p>
            </FormSection>
          ) : null}
        </CollectionForm>
      </div>
    </PageContent>
  );
}

function RefreshCard({ storeId, row }: { storeId: string; row: Collection }) {
  const { t } = useTranslation('collections');
  const format = useFormat();
  const refresh = useRefreshCollection(storeId);
  return (
    <FormSection title={t('sections.rules')}>
      <p className="text-muted-foreground text-sm">
        {row.rulesRefreshedAt ? (
          <span title={format.utc(row.rulesRefreshedAt)}>
            {t('rules.lastRefreshed', { when: format.relative(row.rulesRefreshedAt) })}
          </span>
        ) : (
          t('rules.neverRefreshed')
        )}
      </p>
      <Can permission="collections.write">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={refresh.isPending}
          onClick={() => refresh.mutate(row.id)}
        >
          <RefreshCw aria-hidden className={refresh.isPending ? 'animate-spin' : undefined} />
          {refresh.isPending ? t('actions.refreshing') : t('actions.refresh')}
        </Button>
      </Can>
    </FormSection>
  );
}

function DangerZone({ storeId, row }: { storeId: string; row: Collection }) {
  const { t } = useTranslation('collections');
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<Collection | null>(null);
  return (
    <FormSection title={t('danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('danger.description')}</p>
      <Button
        type="button"
        variant="outline"
        className="text-destructive hover:text-destructive w-full"
        onClick={() => setDeleting(row)}
      >
        <Trash2 aria-hidden />
        {t('actions.delete')}
      </Button>
      <DeleteCollectionDialog
        storeId={storeId}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onDeleted={() => {
          // The record is gone; a dirty form must not keep us on its page.
          releaseSaveBar();
          void navigate({ to: '/s/$storeId/collections', params: { storeId } });
        }}
      />
    </FormSection>
  );
}

function CollectionEdit() {
  const { t } = useTranslation('collections');
  const format = useFormat();
  const store = useStore();
  const { can } = usePermissions();
  const { collectionId } = useParams({ from: EDIT_ROUTE });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: row } = useCollection(store.id, collectionId);
  const products = useCollectionProducts(store.id, collectionId);
  const update = useUpdateCollection(store.id);
  const { kindOf } = useRuleFields();
  if (!row) return null;

  return (
    <PageContent>
      <PageHeader
        title={row.title}
        meta={
          <>
            <CollectionKindBadge kind={row.kind} />
            <CollectionStatusBadge status={row.status} />
          </>
        }
        description={
          <span title={format.utc(row.updatedAt)}>
            {t('updatedAt', { when: format.relative(row.updatedAt) })}
            {' · '}
            <code>{row.handle}</code>
            {' · '}
            {t('columns.productCount', { count: row.productCount })}
          </span>
        }
        backTo={{ to: '/s/$storeId/collections', params: { storeId: store.id } }}
        backLabel={t('title')}
        actions={<ViewOnStorefrontButton url={row.storefrontUrl} live={row.status === 'active'} />}
      />
      <CollectionForm
        key={row.updatedAt}
        storeId={store.id}
        currency={store.defaultCurrency}
        mode="edit"
        defaultValues={fromCollection(row, kindOf)}
        isSubmitting={update.isPending}
        onSubmit={(values, resolveKind) =>
          update.mutateAsync({ id: row.id, body: toCollectionUpdate(values, resolveKind) })
        }
        aside={row.kind === 'automated' ? <RefreshCard storeId={store.id} row={row} /> : null}
        dangerZone={
          <Can permission="collections.write">
            <DangerZone storeId={store.id} row={row} />
          </Can>
        }
      >
        {row.kind === 'manual' ? (
          products.data ? (
            <ManualProductsSection
              storeId={store.id}
              collectionId={row.id}
              items={products.data}
              canWrite={can('collections.write')}
            />
          ) : products.isError ? (
            <FormSection title={t('sections.products')}>
              <ErrorState error={products.error} onRetry={() => void products.refetch()} compact />
            </FormSection>
          ) : (
            <FormSection title={t('sections.products')}>
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </FormSection>
          )
        ) : (
          <CollectionProductsList storeId={store.id} collectionId={row.id} />
        )}
      </CollectionForm>
    </PageContent>
  );
}

export function CollectionCreatePage() {
  return (
    <FeatureErrorBoundary name="collections.create">
      <CollectionCreate />
    </FeatureErrorBoundary>
  );
}

export function CollectionEditPage() {
  return (
    <FeatureErrorBoundary name="collections.edit">
      <CollectionEdit />
    </FeatureErrorBoundary>
  );
}
